import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

const RELOAD_FLAG = 'flownotes:chunk-reload'

/** Detect Vite/Rollup dynamic import failures after a new deploy. */
export function isChunkLoadError(error: unknown): boolean {
  if (error instanceof TypeError) {
    const msg = error.message.toLowerCase()
    if (msg.includes('failed to fetch dynamically imported module')) return true
    if (msg.includes('error loading dynamically imported module')) return true
  }
  if (error instanceof Error) {
    const msg = error.message.toLowerCase()
    if (msg.includes('loading chunk') && msg.includes('failed')) return true
    if (msg.includes('importing a module script failed')) return true
  }
  return false
}

/** Reload once per session when a hashed chunk 404s (SPA fallback returns HTML). */
export function attemptChunkReload(): boolean {
  if (sessionStorage.getItem(RELOAD_FLAG)) return false
  sessionStorage.setItem(RELOAD_FLAG, '1')
  window.location.reload()
  return true
}

export function clearChunkReloadFlag(): void {
  sessionStorage.removeItem(RELOAD_FLAG)
}

export function registerChunkLoadRecovery(): void {
  clearChunkReloadFlag()

  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault()
    attemptChunkReload()
  })
}

export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch((error) => {
      if (isChunkLoadError(error) && attemptChunkReload()) {
        return new Promise<{ default: T }>(() => {})
      }
      throw error
    })
  )
}
